import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FieldRow } from "@/components/admin/FormFields";
import { getSettings, saveSettings } from "@/lib/apiData";

const WIDE = ["textarea", "list", "image", "lines", "multicheck"];

export const SettingsForm = ({ sectionKey, title, description, groups, options }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings", sectionKey], queryFn: () => getSettings(sectionKey) });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await saveSettings(sectionKey, form);
      qc.setQueryData(["settings", sectionKey], res);
      qc.invalidateQueries({ queryKey: ["site-config"] });
      toast.success(`${title} saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid={`settings-${sectionKey}`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-3xl font-black text-neutral-900">{title}</h2>
          {description && <p className="text-neutral-500 text-sm mt-1 max-w-2xl">{description}</p>}
        </div>
        <Button type="submit" disabled={!form || saving} data-testid={`settings-${sectionKey}-save`} className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </Button>
      </div>
      {!form ? <p className="text-neutral-400">Loading…</p> : (
        <div className="space-y-6">
          {groups.map((g) => (
            <section key={g.title} className="bg-white border border-neutral-200 p-6">
              <h3 className="font-display text-xl font-bold text-neutral-900">{g.title}</h3>
              {g.help && <p className="text-sm text-neutral-500 mt-1">{g.help}</p>}
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                {g.fields.map((f) => (
                  <div key={f.name} className={WIDE.includes(f.type) ? "md:col-span-2" : ""}>
                    <FieldRow field={f} value={form[f.name]} options={options} testId={`${sectionKey}-${f.name}`} onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))} />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </form>
  );
};
